import type { Metadata } from "next";

import { publicNakshatraSlug } from "./nakshatra-ids";
import {
  NAKSHATRA_DATA,
  capitalize,
  nakshatraRashiLabel,
  nakshatraRashiSpans,
} from "./nakshatra-page-data";
import { rule76SnippetPatch } from "./rule76-snippet-patches";

import { OPEN_GRAPH_IMAGES } from "@/lib/seo/social-metadata";
export type Nakshatra = (typeof NAKSHATRA_DATA)[number];

// One slug rule: defined in the island-safe ./nakshatra-ids, re-exported for
// the hub, the detail page, NakshatraStudio and their tests.
export { publicNakshatraSlug };

export const NAKSHATRA_MAP = new Map(
  NAKSHATRA_DATA.flatMap((nakshatra) => [
    [nakshatra.id, nakshatra],
    [publicNakshatraSlug(nakshatra.id), nakshatra],
  ])
);

function rashiClause(nakshatra: Nakshatra): string {
  return nakshatraRashiSpans(nakshatra).length === 1
    ? `in ${nakshatraRashiLabel(nakshatra)} rashi`
    : `spanning ${nakshatraRashiLabel(nakshatra)}`;
}

/**
 * Build metadata around the dominant "<name> nakshatra lord" intent while
 * allowing the evidence-bound rule-76 cohort to override an exact member.
 */
export function nakshatraRouteMetadata(nakshatra: Nakshatra): Metadata {
  const slug = publicNakshatraSlug(nakshatra.id);
  const governedSnippet = rule76SnippetPatch(`nakshatra/${slug}`);
  const title =
    governedSnippet?.title ??
    `${nakshatra.name_en} Nakshatra Lord: ${capitalize(nakshatra.ruler)} — Gana & Deity`;
  const description =
    governedSnippet?.meta ??
    // The answer and nothing after it: the closing sentence every one of the
    // 27 pages shared ("It sits in … with all 4 padas and the personality
    // traits this birth star gives.") went on 2026-09-30 (it ran the
    // description past 155 characters on 13 of them). The nine nakshatras that
    // cross a sign cusp name both rashis with the degree split ("spanning Aries
    // 26°40' → Taurus 10°0'"); `rashi_en` alone named only the first.
    `${nakshatra.name_en} nakshatra lord is ${capitalize(nakshatra.ruler)}, deity ${nakshatra.deity}, gana ${nakshatra.gana}, ${rashiClause(nakshatra)}.`;
  return {
    title,
    description,
    alternates: { canonical: `https://ishvaram.com/nakshatra/${slug}/` },
    openGraph: {
      title,
      description,
      siteName: "Ishvaram",
      type: "website",
      images: OPEN_GRAPH_IMAGES,
    },
  };
}
